// src/public/tests/LogicAbstractExamples.js
import { useState } from 'react';
import {
    Container, Paper, Typography, Button, Box, Alert,
    Divider, LinearProgress, RadioGroup, FormControlLabel, Radio
} from '@mui/material';
import { CheckCircle, Cancel, PlayArrow } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const examples = [
    { 
        id: 'E1', 
        question: 'Gatos, perros, animales',
        correctAnswer: 'A',
        explanation: 'Tanto gatos como perros son animales, pero no son lo mismo. Dos subconjuntos dentro de un conjunto general.'
    },
    { 
        id: 'E2', 
        question: 'Camisas, prendas de vestir, pantalones',
        correctAnswer: 'E',
        explanation: 'Camisas ⊂ prendas de vestir; pantalones son otro tipo de prenda independiente.'
    },
    { 
        id: 'E3', 
        question: 'Frutas, manzanas, naranjas',
        correctAnswer: 'A',
        explanation: 'Manzanas y naranjas son tipos de frutas que no se cruzan entre sí.'
    }
];

const LogicAbstractExamples = ({ onStartTest, loading }) => {
    const { t } = useTranslation();
    const [answers, setAnswers] = useState({});
    const [feedback, setFeedback] = useState({});

    // Cargar imágenes dinámicamente
    const imagesCtx = require.context('../assets/logic-abstract', false, /\.(jpg|jpeg|png)$/);
    const imageIndex = imagesCtx.keys().reduce((acc, key) => {
        const base = key.replace('./', '').replace(/\.(jpg|jpeg|png)$/, '');
        acc[base] = imagesCtx(key);
        return acc;
    }, {});

    const handleAnswerChange = (exampleId, value) => {
        setAnswers(prev => ({
            ...prev,
            [exampleId]: value
        }));

        // Verificar respuesta inmediatamente
        const example = examples.find(e => e.id === exampleId);
        const isCorrect = value === example.correctAnswer;
        
        setFeedback(prev => ({
            ...prev,
            [exampleId]: {
                isCorrect,
                correctAnswer: example.correctAnswer,
                explanation: example.explanation
            }
        }));
    };

    // Obtener imágenes para las opciones (usamos las del set 1-15)
    const getImagesForOptions = () => {
        const options = ['A', 'B', 'C', 'D', 'E'];
        const prefix = '1-15'; // Usamos las primeras imágenes como ejemplo
        
        return options.map((opt) => {
            const imageName = `${prefix}${opt}`;
            return {
                option: opt,
                imageName,
                imageUrl: imageIndex[imageName] ?? null,
            };
        });
    };

    const images = getImagesForOptions();

    return (
        <Container maxWidth="lg">
            <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
                <Typography variant="h4" gutterBottom align="center">
                    {t('reasoningTests.practiceExamples')}
                </Typography>
                <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
                    {t('reasoningTests.practiceExamplesDescription')}
                </Typography>

                <Box sx={{ mb: 4 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t('reasoningTests.example')} 1-3 {t('reasoningTests.of')} 3
                    </Typography>
                    <LinearProgress variant="determinate" value={100} sx={{ height: 8, borderRadius: 1 }} />
                </Box>

                {examples.map((example, idx) => (
                    <Box key={example.id} sx={{ mb: idx < examples.length - 1 ? 4 : 0 }}>
                        <Typography variant="h6" color="primary" gutterBottom>
                            {t('reasoningTests.example')} {idx + 1}
                        </Typography>

                        <Typography variant="subtitle1" sx={{ mb: 3 }}>
                            {example.question}
                        </Typography>

                        {/* Grid de imágenes con radio buttons */}
                        <RadioGroup
                            value={answers[example.id] || ''}
                            onChange={(e) => handleAnswerChange(example.id, e.target.value)}
                        >
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
                                    gap: 2,
                                    width: '100%',
                                    maxWidth: '100%',
                                    mb: 2
                                }}
                            >
                                {images.map(({ option, imageUrl, imageName }) => {
                                    const isSelected = answers[example.id] === option;

                                    return (
                                        <Box
                                            key={option}
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'space-between',
                                                borderWidth: isSelected ? 3 : 1,
                                                borderStyle: 'solid',
                                                borderColor: isSelected ? 'primary.main' : 'divider',
                                                borderRadius: 2,
                                                p: 2,
                                                cursor: feedback[example.id]?.isCorrect ? 'not-allowed' : 'pointer',
                                                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                                                backgroundColor: 'transparent',
                                                boxShadow: isSelected ? 2 : 0,
                                                '&:hover': !feedback[example.id]?.isCorrect
                                                    ? { borderColor: 'primary.main', boxShadow: 2 }
                                                    : {},
                                            }}
                                            onClick={() => !feedback[example.id]?.isCorrect && handleAnswerChange(example.id, option)}
                                        >
                                            {imageUrl ? (
                                                <Box
                                                    sx={{
                                                        width: '100%',
                                                        minHeight: 180,
                                                        maxHeight: 220,
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        mb: 2,
                                                    }}
                                                >
                                                    <Box
                                                        component="img"
                                                        src={imageUrl}
                                                        alt={`Opción ${option}`}
                                                        sx={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'contain',
                                                            borderRadius: 1,
                                                        }}
                                                    />
                                                </Box>
                                            ) : (
                                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        No img: {imageName}
                                                    </Typography>
                                                </Box>
                                            )}

                                            <FormControlLabel
                                                value={option}
                                                control={
                                                    <Radio
                                                        disabled={feedback[example.id]?.isCorrect}
                                                        sx={{
                                                            color: 'text.secondary',
                                                            '&.Mui-checked': { color: 'primary.main' },
                                                        }}
                                                    />
                                                }
                                                label={
                                                    <Typography variant="body1">
                                                        <strong>{option}</strong>
                                                    </Typography>
                                                }
                                                sx={{ display: 'flex', justifyContent: 'center', m: 0, pt: 1 }}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </Box>
                                    );
                                })}
                            </Box>
                        </RadioGroup>

                        {feedback[example.id] && (
                            <Alert 
                                severity={feedback[example.id].isCorrect ? 'success' : 'error'}
                                icon={feedback[example.id].isCorrect ? <CheckCircle /> : <Cancel />}
                                sx={{ mt: 2 }}
                            >
                                {feedback[example.id].isCorrect ? (
                                    <>
                                        <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                            {t('reasoningTests.correct')}
                                        </Typography>
                                        <Typography variant="body2">
                                            {feedback[example.id].explanation}
                                        </Typography>
                                    </>
                                ) : (
                                    <>
                                        <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                            {t('reasoningTests.incorrect')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            {t('reasoningTests.correctAnswerIs')}: <strong>{feedback[example.id].correctAnswer}</strong>
                                        </Typography>
                                        <Typography variant="body2">
                                            {feedback[example.id].explanation}
                                        </Typography>
                                    </>
                                )}
                            </Alert>
                        )}

                        {/* Divider entre ejemplos */}
                        {idx < examples.length - 1 && (
                            <Divider sx={{ mt: 3 }} />
                        )}
                    </Box>
                ))}

                <Divider sx={{ my: 4 }} />

                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        {t('reasoningTests.readyToStart')}
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<PlayArrow />}
                        onClick={onStartTest}
                        disabled={loading}
                        sx={{ minWidth: '200px' }}
                    >
                        {t('reasoningTests.startTest')}
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
};

export default LogicAbstractExamples;